/* eslint-disable no-console */
import { getCampaign, partnerOfferingSince, pickOffering } from 'common/utils/partnerCampaign';
import { useCallback } from 'react';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { RevenueCatUI, PAYWALL_RESULT, PaywallPresentationConfiguration } from '@revenuecat/purchases-capacitor-ui';
import { Adjust, AdjustEvent } from '@awesome-cordova-plugins/adjust';
import { trackAdjustEvent, AdjustEventToken } from 'common/utils/adjust';
import toast from 'react-hot-toast';
import { useAppSelector } from 'common/hooks/state';
import { parseObjectPropertiesToCamelCase } from 'common/utils/helpers';
import { linkPath, isBypassEmail } from 'common/constants';
import { MixpanelEventName, mixpanelCustomEvent, trackEvent } from 'common/utils/mixpanel/eventTriggers';
import { buildNativeMixpanelPayload } from './paywallEvents';
import { rememberTrialVariant, trialVariantForAdjust } from './trialVariant';
import {
  syncAndroidPurchases, hasProEntitlement, recordPaywallErrorAndCheckBudget, paywallErrorBudgetExhausted,
  restoreGaveEntitlement,
} from './syncPurchases';
import { markPendingGuestPurchase } from './pendingGuestPurchase';

// Pause between a dismissed paywall (or exit offer) and the re-present.
export const REPRESENT_DELAY_MS = 400;
const waitBeforeRepresent = () => new Promise<void>((resolve) => { setTimeout(resolve, REPRESENT_DELAY_MS); });

export type PaywallOutcome =
  | { result: 'purchased'; productIdentifier: string }
  | { result: 'restored' }
  | { result: 'cancelled' }
  | { result: 'fallback'; reason: 'config_error' | 'sdk_error' };

let inFlightPaywall: Promise<PaywallOutcome> | null = null;

const isConfigError = (err: any): boolean => {
  const msg = (err?.message || String(err)).toLowerCase();
  return msg.includes('paywall') || msg.includes('offering');
};

const goToFallback = (
  navigate: NavigateFunction,
  reason: 'config_error' | 'sdk_error',
): PaywallOutcome => {
  navigate('/pricing', { state: { skipRC: true } });
  return { result: 'fallback', reason };
};

async function presentNativePaywall(
  userProfileData: any,
  navigate: NavigateFunction,
  useFullscreen: boolean,
  hasBackendSubscription: boolean,
  source: string,
  guestToken?: string | null,
): Promise<PaywallOutcome> {
  const isUserBypassEmail = isBypassEmail(userProfileData?.email);

  // Backstop: if the backend (now RevenueCat-backed) already recognises this
  // user as subscribed, never present the hard paywall — even if the on-device
  // SDK hasn't synced their entitlement yet. Prevents hard-looping a real
  // subscriber when getCustomerInfo is stale or the appUserID is out of sync.
  if (hasBackendSubscription) {
    return { result: 'cancelled' };
  }

  // Guest purchases happen before there is an account to attach them to.
  // Remember that one is waiting so sign-up is steered to on every launch
  // until it happens (see pendingGuestPurchase.ts).
  const isGuestPurchase = !!guestToken && !userProfileData?.id;
  const rememberGuestPurchase = async () => {
    if (!isGuestPurchase) return;
    await markPendingGuestPurchase();
    navigate('/signup', { replace: true, state: { guestPurchase: true } });
  };

  // This session has already hit the ERROR budget: go straight to /pricing
  // (Restore purchases lives there) rather than presenting the sheet again.
  if (paywallErrorBudgetExhausted()) {
    return goToFallback(navigate, 'sdk_error');
  }

  try {
    // Android: push any Play purchase that never reached RevenueCat before
    // deciding whether to present at all. If it turns up, there is nothing
    // to gate - presentPaywallIfNeeded would return NOT_PRESENTED, but the
    // explicit check saves the round trip and the flash of a sheet.
    const syncedInfo = await syncAndroidPurchases(`paywall:${source}`);
    if (hasProEntitlement(syncedInfo)) {
      trackEvent(MixpanelEventName.purchaseRestored, { platform: 'native', surface: 'sync_purchases' });
      return { result: 'restored' };
    }

    // Campaign attribution — if the user got here via the event QR code, the
    // deep-link listener in AppLifecycleHandler stashed a token in
    // sessionStorage. Push it onto the RC subscriber attribute and force an
    // offerings refresh so the dashboard targeting rule has a chance to swap
    // `offerings.current` to the event offering BEFORE we read it.
    try {
      const campaign = getCampaign();
      if (campaign) {
        await Purchases.setAttributes({ campaign, $campaign: campaign });
        await Purchases.syncAttributesAndOfferingsIfNeeded();
      }
    } catch (e) {
      // Attribute sync is best-effort — never block the paywall on it.
      // eslint-disable-next-line no-console
      console.warn('[paywall] campaign attribute sync failed', e);
    }

    const offerings = await Purchases.getOfferings();
    // A partner's own offering when they came from one (partnerCampaign.ts).
    let offering = pickOffering(offerings);
    let offeringsPackages = offering?.availablePackages ?? [];

    // Which offering (and so which trial length) RevenueCat chose for this
    // person - an experiment variant, a targeting rule, or just the default.
    // Remembered before the view event so the event and everything after it
    // carries it.
    let trialVariant = rememberTrialVariant(offering);

    trackEvent(MixpanelEventName.paywallViewed, {
      platform: 'native',
      presentation: useFullscreen ? 'fullscreen' : 'modal',
      source,
      ...trialVariant,
    });
    // Adjust gets this one because RevenueCat cannot see it - it only knows
    // about people who actually bought. Fired for guests too: the paywall shows
    // before signup, so gating it on a user id would drop most of them. The
    // variant rides along so ad reports can be split by trial length.
    trackAdjustEvent(AdjustEventToken.viewedPaywall, { source, ...trialVariantForAdjust(trialVariant) });

    // Hard paywall: re-present on dismiss until the user subscribes (or is otherwise allowed
    // to leave). iOS modal sheets are swipe-dismissable by system default; RC's SDK does not
    // expose an option to disable that on our installed version. Re-presenting on cancel
    // enforces the gate. Sequential awaits inside this loop are intentional — each iteration
    // must wait for the prior user action to resolve before deciding whether to re-present.
    //
    // presentPaywallIfNeeded does a fresh customerInfo check internally and returns
    // NOT_PRESENTED if the user already has the 'Doqit Pro' entitlement.
    //
    // Exit offer: the close button is shown for everyone so that closing the paywall
    // triggers the exit offer configured in the RevenueCat dashboard (needs SDK 12.0.3+,
    // we are on 13.0.1). The native SDK presents the exit-offer paywall itself and hands
    // the pending result over to it, so presentPaywallIfNeeded resolves once, with
    // CANCELLED, only after the exit offer has also been closed (or PURCHASED if they
    // bought from it). The gate stays hard: CANCELLED re-presents the main paywall after
    // a short pause so the closing exit-offer sheet finishes animating first. For V2
    // paywalls the close button itself is set in the dashboard editor;
    // displayCloseButton covers original-template paywalls.
    const maxReentries = 100;
    /* eslint-disable no-await-in-loop */
    for (let iteration = 0; iteration < maxReentries; iteration += 1) {
      // Tagged by a partner link since this opened: show their paywall from the
      // next re-show on (partnerCampaign.ts).
      if (iteration > 0) {
        const switched = await partnerOfferingSince(offering, () => Purchases.getOfferings());
        if (switched) {
          offering = switched;
          offeringsPackages = switched.availablePackages ?? [];
          trialVariant = rememberTrialVariant(switched);
        }
      }
      const { result } = await RevenueCatUI.presentPaywallIfNeeded({
        offering: offering ?? undefined,
        requiredEntitlementIdentifier: 'Doqit Pro',
        displayCloseButton: true,
        ...(useFullscreen
          ? { presentationConfiguration: PaywallPresentationConfiguration.FULL_SCREEN }
          : {}),
      });

      if (result === PAYWALL_RESULT.NOT_PRESENTED) {
        // User has 'Doqit Pro' — paywall skipped by SDK. Treat as a successful "no-op cancel".
        return { result: 'cancelled' };
      }

      if (result === PAYWALL_RESULT.PURCHASED) {
        try {
          const { customerInfo } = await Purchases.getCustomerInfo();
          const productIdentifier = customerInfo?.entitlements?.active?.['Doqit Pro']?.productIdentifier ?? '';

          if (productIdentifier && userProfileData?.id) {
            const mixpanelProps = buildNativeMixpanelPayload({
              userProfileData,
              productIdentifier,
              offeringsPackages,
              trialVariant,
            });
            mixpanelCustomEvent({
              mixpanelProps,
              id: userProfileData.id.toString(),
              eventName: MixpanelEventName.purchaseAppPackage,
            });

            try {
              const adjustEvent = new AdjustEvent('7k5qpf');
              adjustEvent.addCallbackParameter('first_name', `${userProfileData.firstName}`);
              if (trialVariant.offering) adjustEvent.addCallbackParameter('offering', trialVariant.offering);
              if (trialVariant.trial_days !== undefined) {
                adjustEvent.addCallbackParameter('trial_days', String(trialVariant.trial_days));
              }
              adjustEvent.setProductId(productIdentifier);
              Adjust.trackEvent(adjustEvent);
            } catch (adjustError) {
              console.error('Adjust tracking error:', adjustError);
            }
          }

          // Guest funnel: the purchase can happen before the guest has signed
          // up (no userProfileData.id yet), so this is gated independently.
          if (productIdentifier && guestToken) {
            mixpanelCustomEvent({
              mixpanelProps: { is_guest: true, platform: 'native', ...trialVariant },
              id: guestToken,
              eventName: MixpanelEventName.guestPurchaseCompleted,
            });
          }

          toast.success('Purchase Successful');
          await rememberGuestPurchase();
          return { result: 'purchased', productIdentifier };
        } catch (e) {
          console.error('Tracking error after purchase:', e);
          toast.success('Purchase Successful');
          await rememberGuestPurchase();
          return { result: 'purchased', productIdentifier: '' };
        }
      }

      if (result === PAYWALL_RESULT.RESTORED) {
        // RESTORED also comes back after a restore that was cancelled at
        // Apple's sign-in or found nothing (see restoreGaveEntitlement). Only
        // an active entitlement counts; otherwise it is handled like a
        // cancel: nothing remembered for a guest, and the paywall comes back.
        if (await restoreGaveEntitlement()) {
          trackEvent(MixpanelEventName.purchaseRestored, { platform: 'native', surface: 'rc_hosted' });
          await rememberGuestPurchase();
          return { result: 'restored' };
        }
        trackEvent(MixpanelEventName.purchaseRestoreFailed, {
          platform: 'native',
          surface: 'rc_hosted',
          reason: 'no_entitlement',
          reentry_iteration: iteration,
        });
        if (isUserBypassEmail) {
          return { result: 'cancelled' };
        }
        await waitBeforeRepresent();
        // eslint-disable-next-line no-continue
        continue;
      }

      if (result === PAYWALL_RESULT.ERROR) {
        trackEvent(MixpanelEventName.purchaseFailed, {
          reason: 'config_error',
          platform: 'native',
          surface: 'rc_hosted',
          error_iteration: iteration,
        });
        // On Android a purchase Play rejected inside the sheet ("already
        // owned": the plan-change refusal when an earlier purchase never
        // reached RevenueCat) comes back as ERROR once the sheet closes. A
        // sync is the one thing that can fix that, so it runs before the
        // single re-present the session budget allows. Beyond that the sheet
        // is never shown again this session: /pricing has Restore purchases.
        const mayRetry = recordPaywallErrorAndCheckBudget();
        const afterError = await syncAndroidPurchases(`paywall_error:${source}`);
        if (hasProEntitlement(afterError)) {
          trackEvent(MixpanelEventName.purchaseRestored, { platform: 'native', surface: 'sync_purchases' });
          await rememberGuestPurchase();
          return { result: 'restored' };
        }
        if (mayRetry) {
          // eslint-disable-next-line no-continue
          continue;
        }
        return goToFallback(navigate, 'config_error');
      }

      // CANCELLED: user dismissed without purchasing. Subscribed users never reach this
      // branch (NOT_PRESENTED handled above). Demo / bypass emails exit cleanly; everyone
      // else loops and re-presents.
      trackEvent(MixpanelEventName.purchaseCancelled, {
        platform: 'native',
        surface: 'rc_hosted',
        reentry_iteration: iteration,
        close_button: true,
      });

      if (isUserBypassEmail) {
        return { result: 'cancelled' };
      }

      // Android: a dismiss can follow a purchase that completed at Play but
      // never posted to RevenueCat. Sync before re-presenting so that person
      // is let through instead of being asked to buy what they already own.
      const afterCancel = await syncAndroidPurchases(`paywall_cancel:${source}`);
      if (hasProEntitlement(afterCancel)) {
        trackEvent(MixpanelEventName.purchaseRestored, { platform: 'native', surface: 'sync_purchases' });
        await rememberGuestPurchase();
        return { result: 'restored' };
      }

      // Let the closing sheet (main paywall or exit offer) finish before the next present.
      await waitBeforeRepresent();
    }
    /* eslint-enable no-await-in-loop */

    // Safety: never spin forever if RC misreports state.
    console.error('[paywall] Exceeded max re-present iterations, falling back');
    return goToFallback(navigate, 'sdk_error');
  } catch (error) {
    console.error('Native paywall error:', error);
    const reason = isConfigError(error) ? 'config_error' : 'sdk_error';
    trackEvent(MixpanelEventName.purchaseFailed, { reason, platform: 'native', surface: 'rc_hosted' });
    return goToFallback(navigate, reason);
  }
}

// The RC web SDK modal paywall is disabled — every web entry point now
// routes to the `/pricing` page (which renders LegacyWebPaywall). The
// `userProfileData` arg is kept so the signature still lines up with
// presentNativePaywall in case we ever revive the modal flow.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function presentWebPaywall(
  _userProfileData: any,
  navigate: NavigateFunction,
  source: string,
): Promise<PaywallOutcome> {
  // The paywallViewed event fires on the /pricing route itself (pricing/index.tsx),
  // which reads paywallSource from navigation state — firing here too would
  // double-count every web open.
  navigate(linkPath.pricing, { state: { paywallSource: source } });
  // Caller's promise resolves immediately; conversion-tracking happens
  // inside LegacyWebPaywall when the user actually purchases.
  return { result: 'cancelled' };
}

export const usePaywall = () => {
  const navigate = useNavigate();
  const userStore = useAppSelector((state: any) => state.auth.user);
  // Optional chaining: the guestSession slice may not exist in every store
  // this hook is tested/rendered against.
  const guestToken = useAppSelector((state: any) => state.guestSession?.token);

  const showPaywall = useCallback((options?: { useFullscreen?: boolean; source?: string }): Promise<PaywallOutcome> => {
    if (inFlightPaywall) {
      return inFlightPaywall;
    }

    const useFullscreen = !!options?.useFullscreen;
    const source = options?.source ?? 'unknown';

    inFlightPaywall = (async (): Promise<PaywallOutcome> => {
      try {
        const userProfileData = parseObjectPropertiesToCamelCase(userStore?.user || userStore);
        const hasBackendSubscription = !!(userStore?.user?.has_subscribed || userStore?.has_subscribed);
        const isNative = Capacitor.isNativePlatform();

        if (isNative) {
          return await presentNativePaywall(userProfileData, navigate, useFullscreen, hasBackendSubscription, source, guestToken);
        }
        return await presentWebPaywall(userProfileData, navigate, source);
      } finally {
        inFlightPaywall = null;
      }
    })();

    return inFlightPaywall;
  }, [navigate, userStore, guestToken]);

  return { showPaywall };
};

// Exported for tests only.
// eslint-disable-next-line no-underscore-dangle
export const __resetInFlightForTests = () => {
  inFlightPaywall = null;
};
